import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { api } from '../api/client.js';
import { useTenantSlug } from '../state/tenant.js';

/**
 * v6-Design: Rechte des Nutzers im aktuellen Mandanten — für das Ausblenden
 * von Navigationszielen, die ohnehin 403 liefern würden. KEIN Sicherheits-
 * mechanismus (das bleiben die Server-Guards), reine Übersichtlichkeit.
 *
 * Solange die Rechte noch laden, gibt `can` true zurück: die Navigation
 * rendert vollständig und dünnt sich ggf. aus. Der umgekehrte Weg (erst
 * leer, dann auffüllen) würde JEDEM Nutzer bei JEDEM Laden ein Flackern
 * zeigen — so flackert es nur für eingeschränkte Nutzer.
 */
export function usePermissions(): { can: (permission?: string) => boolean } {
  const tenantSlug = useTenantSlug();
  const query = useQuery({
    queryKey: ['me', 'permissions', tenantSlug],
    staleTime: 60_000,
    queryFn: () => api<{ permissions: string[] }>('/api/v1/me/permissions'),
  });

  const granted = useMemo(() => new Set(query.data?.permissions ?? []), [query.data]);

  return {
    can: (permission?: string) => {
      if (permission === undefined) return true;
      if (!query.isSuccess) return true;
      return granted.has('*') || granted.has(permission);
    },
  };
}
